import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import JoditEditor from 'jodit-react';
import { Plus, Search, Edit2, Trash2, ExternalLink, ArrowLeft, ImagePlus, X, Star, RefreshCw } from 'lucide-react';
import { adminBlogAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const CATEGORIES = ['Getting Started', 'Features', 'Money Tips', 'AI & Automation', 'Product Updates', 'Guides'];
const MARKETING_BASE = 'https://www.hisabjourney.com';

// Blog images live on Cloudinary (absolute URLs). This is a safety fallback so any
// stray relative /... asset still previews in the admin (a SPA that returns
// index.html for unknown paths).
const resolveAsset = (src = '') => (src.startsWith('/') ? `${MARKETING_BASE}${src}` : src);

const STATUS_BADGE = {
  published: 'bg-green-50 text-green-700',
  draft: 'bg-amber-50 text-amber-700'
};

const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
};

const slugify = (s = '') =>
  s.toString().toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const emptyForm = () => ({
  title: '', slug: '', description: '', content: '', category: 'Guides',
  tags: '', featured: false, cover: '', coverAlt: '', status: 'draft',
  date: new Date().toISOString().slice(0, 10),
  seoTitle: '', seoDescription: '', seoKeywords: ''
});

export default function BlogTab() {
  const { toast } = useToast();
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const editor = useRef(null);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminBlogAPI.getAll({ limit: 100, ...(statusFilter ? { status: statusFilter } : {}), ...(search ? { search } : {}) });
      setBlogs(data.data.blogs || []);
    } catch (err) {
      toast.error(err.error || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, toast]);

  useEffect(() => { if (view === 'list') fetchBlogs(); }, [view, fetchBlogs]);

  const joditConfig = useMemo(() => ({
    readonly: false,
    height: 480,
    toolbarAdaptive: true,
    toolbarSticky: true,
    spellcheck: true,
    placeholder: 'Write your post… use the < > Source button to paste or edit raw HTML',
    // Inline images are embedded as base64 (cover uses Cloudinary). Can move to Cloudinary later.
    uploader: { insertImageAsBase64URI: true },
    buttons: [
      'source', '|', 'bold', 'italic', 'underline', 'strikethrough', '|',
      'ul', 'ol', '|', 'paragraph', 'h1', 'h2', 'h3', '|',
      'link', 'image', 'table', 'hr', '|',
      'align', 'brush', '|', 'undo', 'redo', '|', 'eraser', 'fullsize'
    ]
  }), []);

  const openCreate = () => { setForm(emptyForm()); setEditingId(null); setSlugTouched(false); setView('form'); };

  const openEdit = async (id) => {
    try {
      const { data } = await adminBlogAPI.getById(id);
      const b = data.data.blog;
      setForm({
        title: b.title || '', slug: b.slug || '', description: b.description || '', content: b.content || '',
        category: b.category || 'Guides', tags: (b.tags || []).join(', '), featured: !!b.featured,
        cover: b.cover || '', coverAlt: b.coverAlt || '', status: b.status || 'draft',
        date: b.date ? new Date(b.date).toISOString().slice(0, 10) : '',
        seoTitle: b.seo?.title || '', seoDescription: b.seo?.description || '', seoKeywords: (b.seo?.keywords || []).join(', ')
      });
      setEditingId(id);
      setSlugTouched(true);
      setView('form');
    } catch (err) {
      toast.error(err.error || 'Failed to open blog');
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitleChange = (v) => {
    setForm((f) => ({ ...f, title: v, slug: slugTouched ? f.slug : slugify(v) }));
  };

  const onCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingCover(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await adminBlogAPI.uploadImage(dataUrl);
      setField('cover', data.data.url);
      toast.success('Cover uploaded');
    } catch (err) {
      toast.error(err.error || 'Cover upload failed');
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };

  const buildPayload = () => ({
    title: form.title.trim(),
    slug: form.slug.trim() || slugify(form.title),
    description: form.description.trim(),
    // Prefer the live editor value (onBlur may not have fired before Save)
    content: editor.current?.value ?? form.content,
    category: form.category,
    tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    featured: form.featured,
    cover: form.cover,
    coverAlt: form.coverAlt.trim(),
    status: form.status,
    date: form.date || undefined,
    seo: {
      title: form.seoTitle.trim(),
      description: form.seoDescription.trim(),
      keywords: form.seoKeywords.split(',').map((k) => k.trim()).filter(Boolean)
    }
  });

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await adminBlogAPI.update(editingId, payload);
        toast.success('Blog updated');
      } else {
        await adminBlogAPI.create(payload);
        toast.success('Blog created');
      }
      setView('list');
    } catch (err) {
      toast.error(err.error || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await adminBlogAPI.delete(deleteId);
      toast.success('Blog deleted');
      setDeleteId(null);
      fetchBlogs();
    } catch (err) {
      toast.error(err.error || 'Failed to delete');
    }
  };

  // ---------- FORM VIEW ----------
  if (view === 'form') {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => setView('list')} className="inline-flex items-center gap-1.5 text-sm font-medium text-surface-600 hover:text-surface-900">
            <ArrowLeft className="w-4 h-4" /> Back to list
          </button>
          <div className="flex items-center gap-2">
            <select value={form.status} onChange={(e) => setField('status', e.target.value)} className="px-3 py-2 text-sm border border-surface-200 rounded-xl bg-white">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">Title</label>
                <input value={form.title} onChange={(e) => onTitleChange(e.target.value)} placeholder="Post title" className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">Slug (URL)</label>
                <input value={form.slug} onChange={(e) => { setSlugTouched(true); setField('slug', slugify(e.target.value)); }} placeholder="post-url-slug" className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <p className="text-[11px] text-surface-400 mt-1">{MARKETING_BASE}/blog/{form.slug || 'your-slug'}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">Excerpt / Description</label>
                <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} rows={2} placeholder="Short summary shown in listings" className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">Content</label>
                <div className="rounded-xl overflow-hidden border border-surface-200">
                  <JoditEditor ref={editor} value={form.content} config={joditConfig} onBlur={(newContent) => setField('content', newContent)} />
                </div>
                <p className="text-[11px] text-surface-400 mt-1">Tip: click the <span className="font-mono">{'< >'}</span> Source button in the toolbar to paste or edit raw HTML, then click it again to preview.</p>
              </div>
            </div>

            {/* SEO */}
            <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6 space-y-4">
              <h3 className="text-sm font-semibold text-surface-900">SEO</h3>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">SEO Title</label>
                <input value={form.seoTitle} onChange={(e) => setField('seoTitle', e.target.value)} placeholder="Defaults to post title" className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">SEO Description</label>
                <textarea value={form.seoDescription} onChange={(e) => setField('seoDescription', e.target.value)} rows={2} placeholder="Defaults to excerpt" className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">SEO Keywords (comma separated)</label>
                <input value={form.seoKeywords} onChange={(e) => setField('seoKeywords', e.target.value)} placeholder="budgeting, expense tracker" className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </div>
          </div>

          {/* Sidebar column */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">Category</label>
                <select value={form.category} onChange={(e) => setField('category', e.target.value)} className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">Tags (comma separated)</label>
                <input value={form.tags} onChange={(e) => setField('tags', e.target.value)} placeholder="setup, accounts" className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 mb-1">Display date</label>
                <input type="date" value={form.date} onChange={(e) => setField('date', e.target.value)} className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setField('featured', e.target.checked)} className="w-4 h-4 rounded" />
                <span className="text-sm text-surface-700 inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-500" /> Featured</span>
              </label>
            </div>

            {/* Cover */}
            <div className="bg-white rounded-2xl border border-surface-200 p-4 sm:p-6 space-y-3">
              <label className="block text-xs font-medium text-surface-500">Cover image</label>
              {form.cover ? (
                <div className="relative">
                  <img src={resolveAsset(form.cover)} alt={form.coverAlt} className="w-full h-40 object-cover rounded-xl border border-surface-200" />
                  <button onClick={() => setField('cover', '')} className="absolute top-2 right-2 p-1.5 bg-black/60 text-white rounded-lg hover:bg-black/80"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 h-40 border-2 border-dashed border-surface-200 rounded-xl cursor-pointer hover:border-primary-400 hover:bg-surface-50">
                  {uploadingCover ? <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" /> : <ImagePlus className="w-6 h-6 text-surface-400" />}
                  <span className="text-xs text-surface-500">{uploadingCover ? 'Uploading…' : 'Upload cover'}</span>
                  <input type="file" accept="image/*" onChange={onCoverSelect} className="hidden" />
                </label>
              )}
              <input value={form.coverAlt} onChange={(e) => setField('coverAlt', e.target.value)} placeholder="Cover alt text (accessibility/SEO)" className="w-full px-3 py-2 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- LIST VIEW ----------
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-[15px] sm:text-xl font-semibold text-surface-900">Blog</h2>
        <button onClick={openCreate} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700">
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()); }} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search title, slug, tag…" className="w-full pl-9 pr-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </form>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2.5 bg-white border border-surface-200 rounded-xl text-sm">
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-surface-200 p-3 sm:p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-surface-400" /></div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-12 text-surface-500">
            <p>No blog posts yet</p>
            <button onClick={openCreate} className="mt-3 text-sm font-medium text-primary-600 hover:underline">Create your first post</button>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-3">
              {blogs.map((b) => (
                <div key={b._id} className="border border-surface-200 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-surface-900 text-sm truncate">{b.title}</p>
                      <p className="text-[11px] text-surface-400 truncate">{b.category} · {formatDate(b.date)} · {b.views || 0} views</p>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] font-semibold capitalize ${STATUS_BADGE[b.status]}`}>{b.status}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => openEdit(b._id)} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-surface-700 bg-surface-50 rounded-lg"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                    {b.status === 'published' && <a href={`${MARKETING_BASE}/blog/${b.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg"><ExternalLink className="w-3.5 h-3.5" /></a>}
                    <button onClick={() => setDeleteId(b._id)} className="inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-surface-400 border-b border-surface-100">
                    <th className="py-2 pr-3 font-semibold">Title</th>
                    <th className="py-2 px-3 font-semibold">Category</th>
                    <th className="py-2 px-3 font-semibold">Status</th>
                    <th className="py-2 px-3 font-semibold">Date</th>
                    <th className="py-2 px-3 font-semibold text-right">Views</th>
                    <th className="py-2 pl-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((b) => (
                    <tr key={b._id} className="border-b border-surface-50 last:border-0 hover:bg-surface-50">
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          {b.featured && <Star className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                          <span className="font-medium text-surface-900">{b.title}</span>
                        </div>
                        <span className="text-[11px] text-surface-400 font-mono">/{b.slug}</span>
                      </td>
                      <td className="py-2.5 px-3 text-surface-600">{b.category}</td>
                      <td className="py-2.5 px-3"><span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold capitalize ${STATUS_BADGE[b.status]}`}>{b.status}</span></td>
                      <td className="py-2.5 px-3 text-surface-500">{formatDate(b.date)}</td>
                      <td className="py-2.5 px-3 text-right text-surface-500">{b.views || 0}</td>
                      <td className="py-2.5 pl-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(b._id)} title="Edit" className="p-1.5 text-surface-600 hover:bg-surface-100 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                          {b.status === 'published' && <a href={`${MARKETING_BASE}/blog/${b.slug}`} target="_blank" rel="noreferrer" title="View" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"><ExternalLink className="w-4 h-4" /></a>}
                          <button onClick={() => setDeleteId(b._id)} title="Delete" className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => setDeleteId(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-surface-900">Delete this post?</h3>
            <p className="text-sm text-surface-500 mt-1">This permanently removes the blog post. This cannot be undone.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm font-medium text-surface-600 hover:bg-surface-50 rounded-xl">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
